import { Button } from "@/components/ui/button";
import { UnizenLogo } from "@/components/unizen-logo";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Database,
  Lock,
  ShieldCheck,
  UserCheck,
} from "lucide-react";

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary selection:text-primary-foreground">
      {/* Header Navigation */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5">
            <UnizenLogo className="h-6 w-6" />
            <span className="text-base font-extrabold uppercase tracking-widest text-foreground">
              UNIZEN
            </span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2 text-xs">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:py-16">
        {/* Header Block */}
        <div className="mb-12 border-b border-border/40 pb-8">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <ShieldCheck className="h-3.5 w-3.5" />
            Privacy & Trust
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Last Updated: August 19, 2026 · Effective Date: August 19, 2026
          </p>
        </div>

        {/* Policy Body */}
        <div className="space-y-10 text-sm leading-relaxed text-muted-foreground sm:text-base">
          {/* Section 1 */}
          <section className="space-y-3">
            <h2 className="text-xl font-bold tracking-tight text-foreground">
              1. Overview & Purpose
            </h2>
            <p>
              Welcome to <strong>Unizen</strong> (&ldquo;we&rdquo;,
              &ldquo;our&rdquo;, or &ldquo;us&rdquo;). Unizen is a unified
              student portal and mobile application designed to proxy outdated
              college portals, giving students a modern, fast, and secure
              interface to monitor their attendance, class schedules, grades,
              and biometric logs.
            </p>
            <p>
              Your privacy and security are fundamental to our architecture.
              This Privacy Policy explains what data we collect, how we handle
              and protect it, and the control you have over your personal
              academic information.
            </p>
          </section>

          {/* Section 2 */}
          <section className="space-y-4">
            <h2 className="text-xl font-bold tracking-tight text-foreground">
              2. Information We Collect
            </h2>
            <p>
              To provide our service, we collect and process the following
              categories of information:
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-border/70 bg-card/40 p-5">
                <div className="flex items-center gap-2 font-bold text-foreground mb-2">
                  <UserCheck className="h-5 w-5 text-primary" />
                  Account & Identity Information
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  When you sign in via Google OAuth, we receive your name, email
                  address, and profile picture to create your account session.
                </p>
              </div>

              <div className="rounded-2xl border border-border/70 bg-card/40 p-5">
                <div className="flex items-center gap-2 font-bold text-foreground mb-2">
                  <Lock className="h-5 w-5 text-primary" />
                  College Portal Credentials
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  When linking your college account, your ERP username and
                  password are encrypted immediately on your device/server using
                  AES-256-GCM encryption before storage.
                </p>
              </div>

              <div className="rounded-2xl border border-border/70 bg-card/40 p-5">
                <div className="flex items-center gap-2 font-bold text-foreground mb-2">
                  <Database className="h-5 w-5 text-primary" />
                  Academic Records & Schedules
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Proxied academic data including course enrollments, attendance
                  totals, timetable schedules, internal marks, exam datesheets,
                  and semester CGPA/SGPA.
                </p>
              </div>

              <div className="rounded-2xl border border-border/70 bg-card/40 p-5">
                <div className="flex items-center gap-2 font-bold text-foreground mb-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  Intranet Biometric Logs
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Optional local intranet biometric punch logs
                  (fingerprint/turnstile swipes) fetched directly from campus
                  servers to verify classroom presence.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="space-y-3">
            <h2 className="text-xl font-bold tracking-tight text-foreground">
              3. How We Use Your Information
            </h2>
            <p>We use the collected information exclusively to:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                Proxy and display your real-time attendance percentages,
                timetable countdowns, and marksheet summaries.
              </li>
              <li>
                Calculate attendance insights (e.g., classes needed or available
                skips to maintain 75% criteria).
              </li>
              <li>
                Send scheduled class reminders and low-attendance push
                notifications to your registered device.
              </li>
              <li>
                Ingest and cross-reference biometric swipe timestamps against
                scheduled lecture hours.
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="space-y-3">
            <h2 className="text-xl font-bold tracking-tight text-foreground">
              4. Data Security & AES-256 Encryption
            </h2>
            <p>
              We implement industry-standard security protocols to safeguard
              your personal data:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Vault Encryption:</strong> All stored college
                credentials are encrypted with AES-256-GCM using unique
                initialization vectors. Plaintext credentials are never logged
                or stored.
              </li>
              <li>
                <strong>TLS Transmission:</strong> All API communication between
                your device, our servers, and college portals is encrypted via
                TLS/HTTPS.
              </li>
              <li>
                <strong>No Data Monetization:</strong> We do{" "}
                <strong>not</strong> sell, rent, or trade your personal or
                academic information to third-party advertisers or data brokers.
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="space-y-3">
            <h2 className="text-xl font-bold tracking-tight text-foreground">
              5. Data Retention & Instant Account Wipe
            </h2>
            <p>
              You maintain total ownership of your data. You can unlink your
              college portal account at any time from the app Settings (`DELETE
              /api/college-links/:id`).
            </p>
            <p>
              Unlinking your college portal instantly purges your encrypted
              credentials, cached academic records, timetable entries, and
              biometric logs from our production database.
            </p>
          </section>

          {/* Section 6 */}
          <section className="space-y-3">
            <h2 className="text-xl font-bold tracking-tight text-foreground">
              6. Third-Party Services
            </h2>
            <p>
              Our application integrates with trusted third-party providers for
              basic operations:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Google OAuth:</strong> For secure student
                authentication.
              </li>
              <li>
                <strong>Cloudflare & VPS Infrastructure:</strong> For hosting
                static web assets and API routing with DDoS protection.
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="space-y-3">
            <h2 className="text-xl font-bold tracking-tight text-foreground">
              7. Contact & Data Requests
            </h2>
            <p>
              If you have any questions, concerns, or requests regarding this
              Privacy Policy or wish to request full account deletion, please
              contact us at:
            </p>
            <div className="mt-4 rounded-2xl border border-border/70 bg-card/60 p-5 font-mono text-sm">
              Email:{" "}
              <a
                href="mailto:virexalabss@gmail.com"
                className="text-primary underline"
              >
                virexalabss@gmail.com
              </a>
              <br />
              Website:{" "}
              <a href="https://unizen.app" className="text-primary underline">
                https://unizen.app
              </a>
            </div>
          </section>
        </div>

        {/* Footer Link */}
        <div className="mt-16 border-t border-border/40 pt-8 text-center">
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Return to Unizen
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
